// usePushPermission — registro de push FCM gateado por acceso vigente.
//
// Reglas:
//  - Solo tras login Y hasActiveAccess (el push es un beneficio del plan).
//  - iOS: las notificaciones web solo existen con la PWA INSTALADA
//    (navigator.standalone) — si no está instalada, el caller muestra
//    primero la guía de instalación (InstallBanner).
//  - Sin REACT_APP_FCM_VAPID_KEY configurada → feature apagada (canRequest
//    false), la card no se muestra. Permite deployar la PWA antes de tener
//    la VAPID key.
//  - Token → users/{uid}.pushTokens arrayUnion({token, platform, createdAt}).
import { useCallback, useEffect, useState } from 'react';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { doc, updateDoc, arrayUnion, Timestamp } from 'firebase/firestore';
import { firestore } from '../firebase';
import { useAuth } from '../auth/AuthContext';

const VAPID_KEY = process.env.REACT_APP_FCM_VAPID_KEY || '';
const LS_KEY = 'ee-push-state'; // 'granted' | 'dismissed:<ts>'
const DISMISS_DAYS = 14;

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export default function usePushPermission() {
  const { user, hasActiveAccess } = useAuth();
  const [supported, setSupported] = useState(false);
  const [status, setStatus] = useState('idle'); // idle|prompt|granted|denied|error

  useEffect(() => {
    let alive = true;
    isSupported()
      .then((ok) => { if (alive) setSupported(ok && 'Notification' in window); })
      .catch(() => { if (alive) setSupported(false); });
    return () => { alive = false; };
  }, []);

  const dismissed = (() => {
    try {
      const v = localStorage.getItem(LS_KEY) || '';
      if (v.startsWith('dismissed:')) {
        return Date.now() - Number(v.split(':')[1]) < DISMISS_DAYS * 864e5;
      }
      return false;
    } catch (_e) { return false; }
  })();

  const alreadyGranted =
    typeof Notification !== 'undefined' && Notification.permission === 'granted' &&
    (localStorage.getItem(LS_KEY) || '') === 'granted';

  // ¿Corresponde mostrar la card "Activar alertas"?
  const canRequest = Boolean(
    VAPID_KEY && supported && user && hasActiveAccess &&
    !alreadyGranted && !dismissed &&
    typeof Notification !== 'undefined' && Notification.permission !== 'denied' &&
    (!isIOS() || isStandalone()) // iOS: solo con PWA instalada
  );

  // iOS sin instalar → el caller muestra la guía de instalación primero
  const needsInstallFirst = Boolean(
    VAPID_KEY && user && hasActiveAccess && isIOS() && !isStandalone()
  );

  const request = useCallback(async () => {
    try {
      setStatus('prompt');
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setStatus('denied'); return false; }
      const messaging = getMessaging();
      const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg });
      if (!token) { setStatus('error'); return false; }
      const platform = isIOS() ? 'ios' : (/android/i.test(navigator.userAgent) ? 'android' : 'web');
      await updateDoc(doc(firestore, 'users', user.uid), {
        pushTokens: arrayUnion({ token, platform, createdAt: Timestamp.now() }),
      });
      try { localStorage.setItem(LS_KEY, 'granted'); } catch (_e) { /* noop */ }
      setStatus('granted');
      return true;
    } catch (e) {
      console.warn('[push] registro falló:', e);
      setStatus('error');
      return false;
    }
  }, [user]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(LS_KEY, `dismissed:${Date.now()}`); } catch (_e) { /* noop */ }
    setStatus('idle');
  }, []);

  return { canRequest, needsInstallFirst, status, request, dismiss };
}
